import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  UserPlus,
  CircleNotch,
  DotsThreeVertical,
  Key,
  PencilSimple,
  Prohibit,
  CheckCircle,
  Trash,
  Broom,
} from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  CreateUserDialog, EditUserDialog, ResetPasswordDialog, PurgeDialog,
} from "@/components/admin/UserDialogs";
import { LocationsTab } from "@/components/admin/LocationsTab";
import { RecordsPurgeDialog } from "@/components/admin/RecordsPurgeDialog";
import { DataVerificationTab } from "@/components/admin/DataVerificationTab";
import { MigrationsTab } from "@/components/admin/MigrationsTab";
import { ROLE_LABEL, PresenceDot } from "@/components/admin/constants";

async function loadUsersInto(setUsers) {
  try {
    const { data } = await api.get("/users");
    setUsers(data);
  } catch (e) {
    toast.error(formatApiError(e.response?.data?.detail));
  }
}

function initials(name = "") {
  return name.split(" ").map((n) => n[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
}

function UserRow({ u, me, isGlobalAdmin, onEdit, onReset, onToggle, onDelete }) {
  return (
    <div
      className="grid grid-cols-12 items-center gap-4 border-b border-[#E4E4E7] px-5 py-4 text-sm transition-colors last:border-0 hover:bg-[#FAFAFA]"
      data-testid={`user-row-${u.email}`}
    >
      <div className="col-span-4 flex min-w-0 items-center gap-3">
        {u.profile_picture ? (
          <img src={u.profile_picture} alt="" className="h-8 w-8 shrink-0 rounded-sm object-cover" />
        ) : (
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm bg-[#032852] text-[10px] font-bold text-white">
            {initials(u.name)}
          </span>
        )}
        <div className="min-w-0">
          <p className="truncate font-medium text-[#09090B]">{u.name}</p>
          <p className="truncate text-xs text-[#6D6D70]">{u.job_title || u.email}</p>
        </div>
      </div>
      <div className="col-span-3 hidden truncate text-[#52525B] sm:block">{u.email}</div>
      <div className="col-span-2">
        <span className="text-xs font-medium text-[#52525B]">{ROLE_LABEL[u.role]}</span>
      </div>
      <div className="col-span-2 flex flex-col gap-1">
        {u.user_status === "active" ? (
          <Badge className="w-fit rounded-sm border-0 bg-[#569A1F]/10 text-xs font-semibold text-[#457d18] hover:bg-[#569A1F]/10">
            Active
          </Badge>
        ) : (
          <Badge className="w-fit rounded-sm border-0 bg-[#6D6D70]/10 text-xs font-semibold text-[#6D6D70] hover:bg-[#6D6D70]/10">
            Inactive
          </Badge>
        )}
        <PresenceDot presence={u.presence} testId={`presence-${u.email}`} />
      </div>
      <div className="col-span-1 flex justify-end">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="rounded-sm p-1.5 text-[#6D6D70] hover:bg-[#F4F4F5] hover:text-[#032852]" data-testid={`user-actions-${u.email}`}>
              <DotsThreeVertical size={20} weight="bold" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48 rounded-sm">
            <DropdownMenuItem className="cursor-pointer" onClick={() => onEdit(u)} data-testid={`edit-user-${u.email}`}>
              <PencilSimple size={16} className="mr-2" /> Edit profile
            </DropdownMenuItem>
            <DropdownMenuItem className="cursor-pointer" onClick={() => onReset(u)} data-testid={`reset-user-${u.email}`}>
              <Key size={16} className="mr-2" /> Reset password
            </DropdownMenuItem>
            {u.id !== me?.id && (
              <DropdownMenuItem className="cursor-pointer" onClick={() => onToggle(u)} data-testid={`toggle-user-${u.email}`}>
                {u.user_status === "active" ? (
                  <><Prohibit size={16} className="mr-2 text-[#EF4444]" /> Deactivate</>
                ) : (
                  <><CheckCircle size={16} className="mr-2 text-[#569A1F]" /> Activate</>
                )}
              </DropdownMenuItem>
            )}
            {u.id !== me?.id && isGlobalAdmin && (
              <DropdownMenuItem className="cursor-pointer text-[#EF4444]"
                onClick={() => onDelete(u)}
                data-testid={`delete-user-${u.email}`}>
                <Trash size={16} className="mr-2" /> Delete user
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

const TAB_TRIGGER_CLASS =
  "rounded-sm border border-transparent px-4 text-xs font-bold uppercase tracking-wide " +
  "data-[state=active]:border-[#E4E4E7] data-[state=active]:bg-white data-[state=active]:text-[#032852] data-[state=active]:shadow-sm";

export default function Administration() {
  const { user: me, isGlobalAdmin } = useAuth();
  const [users, setUsers] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [recordsPurgeOpen, setRecordsPurgeOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [resetUser, setResetUser] = useState(null);

  const helpers = useRef({ loadUsersInto });
  const load = useCallback(() => helpers.current.loadUsersInto(setUsers), [helpers, setUsers]);
  useEffect(() => { load(); }, [load]);

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.name}? Login access is removed; historical records keep the attribution "Deleted User — ${u.name}". Active records must be reassigned first.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted — history preserved");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const toggleActive = async (u) => {
    try {
      const path = u.user_status === "active" ? "deactivate" : "activate";
      await api.post(`/users/${u.id}/${path}`);
      toast.success(`${u.name} ${path === "deactivate" ? "deactivated" : "activated"}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="p-6 sm:p-8" data-testid="administration-page">
      <div className="mb-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#569A1F]">Administration</p>
        <h1 className="mt-2 font-heading text-3xl font-black tracking-tight text-[#032852] sm:text-4xl">Administration</h1>
        <p className="mt-2 max-w-2xl text-sm text-[#6D6D70]">
          Users, company locations, and legacy-data verification.
        </p>
      </div>

      <Tabs defaultValue="users">
        <TabsList className="h-auto max-w-full flex-wrap justify-start gap-1 rounded-sm border border-[#E4E4E7] bg-[#F4F4F5] p-1">
          <TabsTrigger value="users" className={TAB_TRIGGER_CLASS} data-testid="admin-tab-users">Users</TabsTrigger>
          <TabsTrigger value="locations" className={TAB_TRIGGER_CLASS} data-testid="admin-tab-locations">Locations</TabsTrigger>
          <TabsTrigger value="verification" className={TAB_TRIGGER_CLASS} data-testid="admin-tab-verification">Data Verification</TabsTrigger>
          {isGlobalAdmin && (
            <TabsTrigger value="migrations" className={TAB_TRIGGER_CLASS} data-testid="admin-tab-migrations">Data Migrations</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="users" className="mt-4">
          <div className="mb-3 flex flex-wrap justify-end gap-2">
            {isGlobalAdmin && (
              <>
                <Button variant="outline" onClick={() => setRecordsPurgeOpen(true)}
                  className="rounded-sm border-[#EF4444]/40 font-semibold text-[#B91C1C] hover:bg-[#EF4444]/5"
                  data-testid="purge-records-btn">
                  <Broom size={16} /> Purge test records
                </Button>
                <Button variant="outline" onClick={() => setPurgeOpen(true)}
                  className="rounded-sm border-[#EF4444]/40 font-semibold text-[#B91C1C] hover:bg-[#EF4444]/5"
                  data-testid="purge-users-btn">
                  <Broom size={16} /> Purge test accounts
                </Button>
              </>
            )}
            <Button
              onClick={() => setCreateOpen(true)}
              className="rounded-sm bg-[#569A1F] font-semibold text-white hover:bg-[#457d18]"
              data-testid="create-user-btn"
            >
              <UserPlus size={18} /> New User
            </Button>
          </div>

          <div className="overflow-hidden rounded-sm border border-[#E4E4E7] bg-white">
            <div className="grid grid-cols-12 gap-4 border-b border-[#E4E4E7] bg-[#F4F4F5] px-5 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-[#6D6D70]">
              <div className="col-span-4">Name</div>
              <div className="col-span-3 hidden sm:block">Email</div>
              <div className="col-span-2">Role</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-1 text-right">Actions</div>
            </div>

            {users === null ? (
              <div className="flex items-center justify-center py-16">
                <CircleNotch size={24} className="animate-spin text-[#032852]" />
              </div>
            ) : users.length === 0 ? (
              <div className="py-16 text-center text-sm text-[#6D6D70]">No users yet.</div>
            ) : (
              users.map((u) => (
                <UserRow key={u.id} u={u} me={me} isGlobalAdmin={isGlobalAdmin}
                  onEdit={setEditUser} onReset={setResetUser}
                  onToggle={toggleActive} onDelete={deleteUser} />
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="locations" className="mt-4">
          <LocationsTab isGlobalAdmin={isGlobalAdmin} />
        </TabsContent>

        <TabsContent value="verification" className="mt-4">
          <DataVerificationTab />
        </TabsContent>

        {isGlobalAdmin && (
          <TabsContent value="migrations" className="mt-4">
            <MigrationsTab />
          </TabsContent>
        )}
      </Tabs>

      <CreateUserDialog open={createOpen} onClose={() => setCreateOpen(false)} onDone={load} />
      {editUser && (
        <EditUserDialog key={editUser.id} user={editUser} onClose={() => setEditUser(null)} onDone={load} />
      )}
      <ResetPasswordDialog user={resetUser} onClose={() => setResetUser(null)} />
      <PurgeDialog open={purgeOpen} onClose={() => setPurgeOpen(false)} onDone={load} />
      <RecordsPurgeDialog open={recordsPurgeOpen} onClose={() => setRecordsPurgeOpen(false)} onDone={load} />
    </div>
  );
}
